import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Rocket, TrendingUp, Building, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/seo/SEO';
import content from '../content.json';

const iconMap = {
  Rocket,
  TrendingUp,
  Building,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function UseCases() {
  const { title, subtitle, cases } = content.useCases;

  return (
    <>
      <SEO 
        title={content.seo.useCases.title}
        description={content.seo.useCases.description}
        canonical="https://dakshtechinfo.uk/use-cases"
      />
      
      <div data-testid="use-cases-page">
        {/* Hero Section */}
        <section className="section-padding bg-muted/30" data-testid="use-cases-hero">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              >
                {title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {subtitle}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Cases Section */}
        <section className="section-padding" data-testid="use-cases-list">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="space-y-12 md:space-y-16"
            >
              {cases.map((caseStudy, index) => {
                const Icon = iconMap[caseStudy.icon] || Rocket;
                const isEven = index % 2 === 0;
                
                return (
                  <motion.div 
                    key={caseStudy.id}
                    variants={itemVariants}
                    className="relative"
                    data-testid={`case-${caseStudy.id}`}
                  >
                    <Card className="overflow-hidden border-border/50">
                      <CardContent className="p-0">
                        <div className={`grid grid-cols-1 lg:grid-cols-2 ${isEven ? '' : 'lg:grid-flow-dense'}`}>
                          {/* Content Side */}
                          <div className={`p-6 md:p-8 lg:p-12 ${isEven ? '' : 'lg:col-start-2'}`}>
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                                <Icon className="h-5 w-5 text-accent" />
                              </div>
                              <span className="text-sm font-medium text-muted-foreground">
                                {caseStudy.industry}
                              </span>
                            </div>
                            
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">
                              {caseStudy.title}
                            </h2>
                            
                            <div className="space-y-6">
                              <div>
                                <h4 className="text-sm font-semibold text-accent uppercase tracking-wide mb-2">
                                  The Challenge
                                </h4>
                                <p className="text-muted-foreground">
                                  {caseStudy.problem}
                                </p>
                              </div>
                              
                              <div>
                                <h4 className="text-sm font-semibold text-accent uppercase tracking-wide mb-2">
                                  Our Approach
                                </h4>
                                <p className="text-muted-foreground">
                                  {caseStudy.approach}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Results Side */}
                          <div className={`bg-muted/50 p-6 md:p-8 lg:p-12 flex flex-col justify-center ${isEven ? '' : 'lg:col-start-1'}`}>
                            <h4 className="text-sm font-semibold text-accent uppercase tracking-wide mb-6">
                              Results Achieved
                            </h4>
                            <ul className="space-y-4">
                              {caseStudy.results.map((result, idx) => (
                                <li 
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                                  <span className="text-foreground font-medium">{result}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Industry Overview */}
        <section className="section-padding bg-muted/30" data-testid="industries-overview">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Industries We Serve
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Our expertise spans across various sectors, helping organisations of all sizes
                </p>
              </motion.div>

              <motion.div 
                variants={itemVariants}
                className="grid grid-cols-2 md:grid-cols-4 gap-6"
              >
                {['FinTech', 'E-commerce', 'Healthcare', 'SaaS', 'Media', 'Manufacturing', 'Education', 'Logistics'].map((industry, index) => (
                  <div 
                    key={index}
                    className="text-center p-6 rounded-xl bg-card border border-border/50 hover:border-accent/50 transition-colors"
                    data-testid={`industry-${industry.toLowerCase()}`}
                  >
                    <span className="font-medium">{industry}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding" data-testid="use-cases-cta">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative overflow-hidden rounded-2xl bg-primary p-8 md:p-12 lg:p-16"
            >
              <div className="relative z-10 max-w-2xl">
                <motion.h2 
                  variants={itemVariants}
                  className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4"
                >
                  Your Success Story Starts Here
                </motion.h2>
                <motion.p 
                  variants={itemVariants}
                  className="text-base text-primary-foreground/80 mb-8"
                >
                  Every organisation is unique. Let's discuss your specific challenges and design a solution that delivers measurable results.
                </motion.p>
                <motion.div variants={itemVariants}>
                  <Button 
                    size="lg" 
                    variant="secondary" 
                    asChild
                    className="group"
                    data-testid="use-cases-contact-cta"
                  >
                    <Link to="/contact">
                      Discuss Your Project
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
